import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Typography } from '@mui/joy';
import { Stepper, Step, StepLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Text, Input } from 'src/mtska-frontend-app-component';
import Rules from '../../../../mtska-frontend-app-component/components/Segment/Rules';
import SegmentationSummary from '../../../../mtska-frontend-app-component/components/Segment/SegmentationSummary';
import SegmentConfirmation from '../../../../mtska-frontend-app-component/components/Segment/SegmentConfirmation';
import List from '../components/ListLead';
import dayjs from 'dayjs';
import { useDataProvider } from "../../../../mtska-frontend-data-provider";

const AudienceSegmentCreator = ({
    externalSetForwardDisabled = () => { },
    externalSetBackDisabled = () => { },
    externalSetActionInvoked = () => { },
    externalSetActionLabel = () => { },
    externalSetErrorMessage = () => { },
    actionInvoked,
    onSave = () => { },
    onFinish = () => { },
    isCompleted = false,
    item
}) => {
    
    const { t } = useTranslation();
    const steps = [t('Info'), t('Rules'), t('Summary'), t('Confirm')];
    const [activeStep, setActiveStep] = useState(0);
    const [mappingValue, setMappingValue] = useState([]);
    const [totalLeads, setTotalLeads] = useState(0);
    const isSaving = useRef(false);
    const rulesRef = useRef([]);
    const [filters, setFilters] = useState({ items: [] });
    const recordKey = item?.id ?? item?._id ?? 'new';

    const { control, setValue, watch, reset } = useForm({
        mode: 'onChange',
        defaultValues: {
            segmentName: '',
            description: '',
            segmentColor: '#E9B712',
            creationDate: dayjs().format('YYYY-MM-DD')
        }
    });

    const { item: config } = useDataProvider('configs/config');
    const moduleConfig = config?.data?.modules?.['audiences/audiencelead'];
    const tableViewColumns = moduleConfig?.tableConfig?.columns || [];
    const modelFields = tableViewColumns
        .map(column => ({ key: column, label: t(column) }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' }));

    const [validationState, setValidationState] = useState({});

    const applyItemToWizard = useCallback((segmentItem) => {
        if (segmentItem?.id || segmentItem?._id) {
            setValue('segmentName', segmentItem.name || '');
            setValue('description', segmentItem.description || '');
            setValue('segmentColor', segmentItem.color || '#E9B712');
            setValue('creationDate', segmentItem.creation_date || dayjs().format('YYYY-MM-DD'));

            const rules = (segmentItem.segmentationRules ?? []).map(rule => ({
                field: rule.field || '',
                operator: rule.operator || '=',
                value: rule.value || ''
            }));
            setMappingValue(rules);
            rulesRef.current = rules;
            setFilters({ items: rules });
            setValue("rules", rules);
            return;
        }

        reset({
            segmentName: '',
            description: '',
            segmentColor: '#E9B712',
            creationDate: dayjs().format('YYYY-MM-DD')
        });
        setMappingValue([]);
        rulesRef.current = [];
        setFilters({ items: [] });
        setValue("rules", []);
        setActiveStep(0);
        setTotalLeads(0);
        setValidationState({});
    }, [reset, setValue]);

    useEffect(() => {
        applyItemToWizard(item);
    }, [recordKey, applyItemToWizard, item]);

    useEffect(() => {
        externalSetBackDisabled(activeStep === 0 || isCompleted);
        externalSetForwardDisabled(isCompleted);
        externalSetActionLabel(activeStep === steps.length - 2 ? t('Submit') : (isCompleted ? t('Close') : t('Next')));
    }, [activeStep, steps.length, t, externalSetBackDisabled, externalSetForwardDisabled, externalSetActionLabel, isCompleted]);

    useEffect(() => {
        if (typeof actionInvoked === 'undefined' || actionInvoked === 0 || isCompleted) return;

        const currentStep = activeStep;
        const totalSteps = steps.length;

        if (actionInvoked > 0) {
            let stepError = null;
            if (currentStep === 0) {
                const segmentNameValue = watch('segmentName');
                if (!segmentNameValue || segmentNameValue.trim() === '') {
                    stepError = t('MandatoryName');
                }
            }

            if (stepError) {
                setValidationState(prev => ({ ...prev, [currentStep]: stepError }));
                externalSetErrorMessage(stepError);
                externalSetActionInvoked(0);
                return;
            } else {
                setValidationState(prev => ({ ...prev, [currentStep]: null }));
                externalSetErrorMessage(null);
            }

            if (currentStep === totalSteps - 2) {
                if (isSaving.current) return;
                isSaving.current = true;
                const formData = {
                    id: item?.id,
                    name: watch('segmentName'),
                    description: watch('description'),
                    color: watch('segmentColor'),
                    creation_date: watch('creationDate'),
                    segmentationRules: rulesRef.current,
                    status: 'draft'
                };
                if (onSave) {
                    onSave(formData)
                        .then(() => {
                            setActiveStep(currentStep + 1);
                            onFinish();
                            externalSetActionInvoked(0);
                            isSaving.current = false;
                        })
                        .catch(error => {
                            externalSetErrorMessage(error.message || t('failedSavingSegment'));
                            externalSetActionInvoked(0);
                            isSaving.current = false;
                        });
                } else {
                    isSaving.current = false;
                    setActiveStep(currentStep + 1);
                    onFinish();
                    externalSetActionInvoked(0);
                }
            } else {
                setActiveStep(currentStep + 1);
                externalSetActionInvoked(0);
            }
        } else if (actionInvoked < 0) {
            if (currentStep > 0) {
                setActiveStep(currentStep - 1);
                externalSetErrorMessage(null);
            }
            externalSetActionInvoked(0);
        }
    }, [actionInvoked, activeStep, steps.length, externalSetActionInvoked, externalSetErrorMessage, watch, onSave, onFinish, t, isCompleted, item?.id]);

    const summaryFields = [
        { label: 'Nome segmento', value: watch('segmentName') || 'N/A' },
        { label: 'Descrizione', value: watch('description') || 'N/A' },
        { label: 'Colore', value: watch('segmentColor') || 'N/A' },
        { label: 'Data creazione', value: watch('creationDate') || 'N/A' },
        { label: 'Totale lead a oggi', value: totalLeads },
    ];

    const handleTotalChange = useCallback((newTotal) => {
        setTotalLeads(newTotal);
    }, []);

    const handleRulesChange = useCallback((newRules) => {
        rulesRef.current = newRules;
        setMappingValue(newRules);
        setFilters({
            items: newRules.map(rule => ({
                field: rule.field,
                operator: rule.operator,
                value: rule.value
            }))
        });
        if (!newRules.length) {
            setTotalLeads(0);
        }
    }, []);

    return (
        <Box className="audiencesegmentcreator">
            <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
                {steps.map((label, index) => {
                    const labelProps = {};
                    const stepError = validationState[index];
                    if (stepError) {
                        labelProps.optional = (
                            <Typography variant="caption" color="error">
                                {stepError}
                            </Typography>
                        );
                        labelProps.error = true;
                    }
                    return (
                        <Step key={label}>
                            <StepLabel {...labelProps}>{t(label)}</StepLabel>
                        </Step>
                    );
                })}
            </Stepper>
            <Box className="form-wrapper">
                <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
                    <fieldset className="mtska-fieldset" style={{ border: 'none' }}>
                        <Text level="title-md" className="mtska-fieldset-title">{t('infoSegment')}</Text>
                        <Box className="form-element text col-12">
                            <Input label={t('segmentName')} type="text" name="segmentName" control={control} editing={true} required={true} />
                        </Box>
                        <Box className="form-element text col-12">
                            <Input label={t('segmentDescription')} type="textarea" name="description" control={control} editing={true} rows={3} />
                        </Box>
                        <Box className="form-element text col-6">
                            <Input label={t('segmentColor')} type="colorpicker" name="segmentColor" control={control} editing={true} />
                        </Box>
                        <Box className="form-element text col-6">
                            <Input label={t('creationDate')} type="date" name="creationDate" control={control} editing={false} />
                        </Box>
                    </fieldset>
                </Box>
                {activeStep === 1 && (
                    <Box sx={{ px: 2 }}>
                        <div className="form-element col-12" style={{ marginBottom: '32px' }}>
                            <Rules
                                key={recordKey}
                                model={modelFields}
                                control={control}
                                defaultRules={mappingValue}
                                onRulesChange={handleRulesChange}
                                setValue={setValue}
                            />
                        </div>
                        <List key={`${recordKey}-${JSON.stringify(filters.items)}`} onTotalChange={handleTotalChange} filters={filters} />
                    </Box>
                )}
                <Box sx={{ display: activeStep === 2 ? 'block' : 'none', px: 2 }}>
                    <SegmentationSummary summaryFields={summaryFields} mappingValue={mappingValue} modelFields={modelFields} isSaving={isSaving} />
                </Box>
                <SegmentConfirmation isCompleted={isCompleted} />
            </Box>
        </Box>
    );
};

export default AudienceSegmentCreator;
